import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  Menu, X, Calendar, Users, ShoppingBag, MessageSquare, 
  Search, Bell, User, Music, Volume2, Headphones, Star 
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import SearchModal from '../ui/SearchModal'
import { getBrandForHost } from '../../branding/brandConfig'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [notifications, setNotifications] = useState(3)
  const location = useLocation()
  const brand = getBrandForHost(window.location.hostname)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const navItems = [
    { name: 'Calendar', path: '/calendar', icon: Calendar, description: 'Live music events' },
    { name: 'Artists', path: '/artists', icon: Users, description: 'Local musicians' },
    { name: 'Marketplace', path: '/marketplace', icon: ShoppingBag, description: 'Buy & sell gear' },
    { name: 'Forums', path: '/forums', icon: MessageSquare, description: 'Community discussions' },
    { name: 'Links', path: '/links', icon: Headphones, description: 'Resources hub' },
    { name: 'Reviews', path: '/reviews', icon: Star, description: 'Gear/venue/artist' },
    { name: 'About', path: brand.aboutRoute, icon: Users, description: 'About this site' },
    { name: 'Partners', path: brand.partnersRoute, icon: Users, description: 'Zippy & LEA' },
  ]

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'glass-nav shadow-2xl shadow-black/20' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="container-max section-padding">
          <div className="flex items-center justify-between h-20 gap-3">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4 group min-w-0">
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-le-accent to-le-accent-2 rounded-2xl flex items-center justify-center shadow-lg shadow-le-accent/30">
                  <Volume2 size={28} className="text-white" />
                </div>
                <div className="absolute -inset-1 bg-gradient-to-br from-le-accent/30 to-le-accent-2/30 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
              <div className="hidden sm:block min-w-0 max-w-[45vw] md:max-w-[38vw] lg:max-w-[30vw] xl:max-w-[28vw] 2xl:max-w-none">
                <h1 className="font-display font-bold text-xl md:text-2xl xl:text-3xl leading-tight text-white group-hover:text-le-accent transition-colors duration-300 truncate">
                  {brand.siteName}
                </h1>
                <p className="hidden xl:block text-sm text-white/60 font-medium truncate">{brand.tagline}</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex flex-1 min-w-0 items-center space-x-1 overflow-hidden">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`nav-link group relative px-3 py-2 rounded-xl transition-all duration-300 ${
                      isActive 
                        ? 'text-le-accent bg-le-accent/10 shadow-lg shadow-le-accent/20' 
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon size={20} />
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="hidden 2xl:block text-xs text-white/50 group-hover:text-white/70 transition-colors duration-200">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-2 xl:space-x-4 flex-shrink-0">
              <motion.button 
                onClick={() => setIsSearchOpen(true)}
                className="relative p-3 text-white/80 hover:text-white transition-colors duration-300 group"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                title="Search (⌘K)"
              >
                <Search size={22} />
                <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-black/90 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  Search (⌘K)
                </div>
              </motion.button>
              
              <motion.button 
                className="relative p-3 text-white/80 hover:text-white transition-colors duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Bell size={22} />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                    {notifications}
                  </span>
                )}
              </motion.button>
              
              <div className="hidden xl:block">
                <motion.button 
                  className="btn-primary flex items-center space-x-3 px-6 py-3"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <User size={20} />
                  <span>Sign In</span>
                </motion.button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="lg:hidden p-3 text-white relative z-10"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={28} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={28} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="lg:hidden glass-nav border-t border-white/10"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="section-padding py-8 space-y-2">
                {navItems.map((item, index) => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.path
                  return (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.path}
                        className={`flex items-center space-x-4 px-6 py-4 rounded-xl transition-all duration-300 ${
                          isActive 
                            ? 'text-le-accent bg-le-accent/10' 
                            : 'text-white/80 hover:text-white hover:bg-white/5'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Icon size={24} />
                        <div>
                          <div className="font-medium text-lg">{item.name}</div>
                          <div className="text-sm text-white/60">{item.description}</div>
                        </div>
                      </Link>
                    </motion.div>
                  )
                })}
                
                <div className="pt-6 border-t border-white/10 space-y-4">
                  <button 
                    onClick={() => setIsSearchOpen(true)}
                    className="w-full btn-secondary flex items-center justify-center space-x-3 py-4"
                  >
                    <Search size={20} />
                    <span>Search</span>
                  </button>
                  <button className="w-full btn-primary flex items-center justify-center space-x-3 py-4">
                    <User size={20} />
                    <span>Sign In</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  )
}

export default Header